import { useState } from "react";
import { ago, api, Empty, fmtDate, Loading, Pill, useLoad, useMe } from "./lib";

const fmt = (v: number | null, f: string) =>
  v == null ? "Unknown" : f === "pct" ? `${Math.round(v * 100)}%` : f === "usd" ? `$${v.toLocaleString("en-US", { maximumFractionDigits: 2 })}` : v.toLocaleString("en-US");

// Analytics: summary numbers from the warehouse, trends, first value, feature adoption, segments, and Claude's read for the roadmap.
export function Analytics() {
  const { toast } = useMe();
  const { data, error, reload } = useLoad<any>("/api/analytics");
  const [busy, setBusy] = useState(false);
  if (!data) return <div className="page"><Loading error={error} /></div>;
  const integ = data.integration;
  const imported = data.sources.find((s: string) => s.startsWith("report:"));
  const sync = async () => {
    setBusy(true);
    try {
      const r = await api("/api/analytics/sync", { method: "POST" });
      toast(r.result === "ok" ? "Synced with BigQuery" : r.integration?.last_error ?? "Not connected", r.result !== "ok");
      reload();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="page">
      <section className="card row between">
        <div className="stack" style={{ gap: 2 }}>
          <strong>Analytics{data.as_of ? ` · through ${fmtDate(data.as_of)}` : ""}</strong>
          <span className="muted small">
            {integ?.status === "ok"
              ? `Live from BigQuery (${integ.last_detail?.source}). Last synced ${ago(integ.last_success_at)}; checks every 6 hours.${integ.last_detail?.missing?.length ? ` Not set up yet: ${integ.last_detail.missing.join(", ")}.` : ""}`
              : imported
                ? `Imported from Dallas's report (${imported.slice(7)}). Live BigQuery isn't connected yet${integ?.status === "error" ? `: ${integ.last_error}` : "; see docs/bigquery-setup.sql"}.`
                : integ?.status === "error" ? integ.last_error : "Not connected yet. See docs/bigquery-setup.sql."}
          </span>
        </div>
        {data.can_sync && <button className="btn sm" disabled={busy} onClick={sync}>{busy ? "Syncing…" : "Sync now"}</button>}
      </section>

      {!data.as_of ? (
        <Empty>No analytics yet.</Empty>
      ) : (
        <>
          <AnalyticsTake />
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 200px), 1fr))" }}>
            {data.headline.map((h: any) => (
              <section key={h.label} className="card stack" style={{ gap: 2 }} title={h.caveat ?? undefined}>
                <span className="muted tiny">{h.label}</span>
                <strong style={{ fontSize: 24 }}>{fmt(h.value, h.fmt)}</strong>
                {h.detail && <span className="muted tiny">{h.detail}</span>}
              </section>
            ))}
          </div>
          {data.caveats.length > 0 && (
            <div className="muted tiny">Provisional: "active" and "came back" count moms who saved something, not app opens, and internal accounts are included. The numbers run low until app opens are tracked.</div>
          )}

          <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))" }}>
            <Bars title="New accounts per day" rows={data.signups.map((x: any) => ({ date: x.date, v: x.signups, tip: `${x.signups} new (${x.ios} iOS, ${x.android} Android)` }))} />
            <Bars title="Moms active per day" rows={data.active.filter((x: any) => !x.partial).map((x: any) => ({ date: x.date, v: x.dau, tip: `${x.dau} active · ${x.wau} that week · ${x.mau} that month` }))} />
          </div>

          <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))" }}>
            <section className="card stack">
              <strong>What new moms do first</strong>
              <span className="muted tiny">Share of accounts that ever did it, and the typical time from signup</span>
              <table className="t">
                <thead><tr><th>Action</th><th>Reached</th><th>Typical time</th></tr></thead>
                <tbody>
                  {data.first_value.filter((x: any) => x.action !== "any_action").map((x: any) => (
                    <tr key={x.action}>
                      <td>{x.action.replace(/_/g, " ")}</td>
                      <td><Meter v={x.share} /></td>
                      <td>{x.median_hours == null ? "—" : x.median_hours < 1 ? "under an hour" : x.median_hours < 48 ? `${Math.round(x.median_hours)} hours` : `${Math.round(x.median_hours / 24)} days`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <section className="card stack">
              <strong>Features against their targets</strong>
              <table className="t">
                <thead><tr><th>Feature</th><th>Measure</th><th>Now</th><th>Target</th></tr></thead>
                <tbody>
                  {data.features.map((x: any, i: number) => (
                    <tr key={i}>
                      <td>{x.feature}</td>
                      <td className="muted small">{x.metric}</td>
                      <td>{x.metric?.includes("per account") ? x.rate : <Meter v={x.rate} />}</td>
                      <td className="muted small">{x.target ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </div>

          <Segments rows={data.segments} />
        </>
      )}
    </div>
  );
}

function Meter({ v }: { v: number | null }) {
  if (v == null) return <span className="muted">—</span>;
  return (
    <span className="row" style={{ gap: 6 }}>
      <span style={{ width: 60, height: 6, borderRadius: 3, background: "var(--line, #eee)", overflow: "hidden", display: "inline-block" }}>
        <span style={{ display: "block", height: "100%", width: `${Math.min(100, v * 100)}%`, background: "var(--accent)", borderRadius: 3 }} />
      </span>
      <span className="small">{Math.round(v * 100)}%</span>
    </span>
  );
}

// One series, one axis, bars anchored to the baseline; hover shows the day's numbers; a table view for reading exact values.
function Bars({ title, rows }: { title: string; rows: { date: string; v: number; tip: string }[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const max = Math.max(1, ...rows.map((r) => r.v));
  const W = 600, H = 160, gap = 2, bw = rows.length ? (W - gap * (rows.length - 1)) / rows.length : 0;
  const h = hover != null ? rows[hover] : null;
  return (
    <section className="card stack">
      <div className="row between">
        <strong>{title}</strong>
        <button className="btn ghost sm" onClick={() => setTable(!table)}>{table ? "Chart" : "Table"}</button>
      </div>
      <span className="muted tiny" style={{ minHeight: 16 }}>{h ? `${fmtDate(h.date)}: ${h.tip}` : rows.length ? `${fmtDate(rows[0]!.date)} to ${fmtDate(rows.at(-1)!.date)} · peak ${max}` : "No data"}</span>
      {table ? (
        <div style={{ maxHeight: 220, overflow: "auto" }}>
          <table className="t"><tbody>{[...rows].reverse().map((r) => <tr key={r.date}><td>{fmtDate(r.date)}</td><td className="small">{r.tip}</td></tr>)}</tbody></table>
        </div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={title} onMouseLeave={() => setHover(null)} style={{ display: "block" }}>
          <line x1={0} x2={W} y1={H - 0.5} y2={H - 0.5} stroke="var(--line, #e5e2ea)" />
          {rows.map((r, i) => {
            const bh = r.v ? Math.max(3, (r.v / max) * (H - 8)) : 0;
            const x = i * (bw + gap);
            return (
              <g key={r.date} onMouseEnter={() => setHover(i)}>
                <rect x={x} y={0} width={bw + gap} height={H} fill="transparent" />
                {bh > 0 && <rect x={x} y={H - bh} width={bw} height={bh} rx={Math.min(4, bw / 2)} fill="var(--accent)" opacity={hover == null || hover === i ? 1 : 0.45} />}
              </g>
            );
          })}
        </svg>
      )}
    </section>
  );
}

function Segments({ rows }: { rows: any[] }) {
  const [type, setType] = useState("persona");
  const [platform, setPlatform] = useState("");
  const list = rows.filter((r) => r.segment_type === type && (!platform || r.platform === platform));
  // Roll months and (unless filtered) platforms together per segment.
  const by = new Map<string, any>();
  for (const r of list) {
    const x = by.get(r.segment) ?? { segment: r.segment, accounts: 0, onboarded: 0, d1: 0, d7: 0, active: 0, paid: 0 };
    x.accounts += r.accounts; x.onboarded += r.onboarded ?? 0; x.d1 += r.d1_retained ?? 0; x.d7 += r.d7_retained ?? 0; x.active += r.active_last_7d ?? 0; x.paid += r.trials_converted ?? 0;
    by.set(r.segment, x);
  }
  const out = [...by.values()].sort((a, b) => b.accounts - a.accounts);
  return (
    <section className="card stack">
      <div className="row between" style={{ flexWrap: "wrap", gap: 8 }}>
        <strong>Which moms get the most out of it</strong>
        <div className="row" style={{ gap: 6 }}>
          <div className="tabs">
            <button className={type === "persona" ? "on" : ""} onClick={() => setType("persona")}>Kind of mom</button>
            <button className={type === "kids_stage" ? "on" : ""} onClick={() => setType("kids_stage")}>Kids' stage</button>
          </div>
          <div className="tabs">
            {[["", "All"], ["ios", "iOS"], ["android", "Android"]].map(([k, l]) => <button key={k} className={platform === k ? "on" : ""} onClick={() => setPlatform(k!)}>{l}</button>)}
          </div>
        </div>
      </div>
      {!rows.length ? (
        <span className="muted small">Needs the portal_segments view, which isn't set up yet. Groups under 5 moms are always left out.</span>
      ) : out.length ? (
        <table className="t">
          <thead><tr><th>Segment</th><th>Accounts</th><th>Onboarded</th><th>Next day</th><th>Day 7</th><th>Active this week</th><th>Paying</th></tr></thead>
          <tbody>
            {out.map((x) => (
              <tr key={x.segment}>
                <td>{String(x.segment).replace(/_/g, " ")}</td>
                <td>{x.accounts}</td>
                <td><Meter v={x.accounts ? x.onboarded / x.accounts : null} /></td>
                <td><Meter v={x.accounts ? x.d1 / x.accounts : null} /></td>
                <td><Meter v={x.accounts ? x.d7 / x.accounts : null} /></td>
                <td><Meter v={x.accounts ? x.active / x.accounts : null} /></td>
                <td>{x.paid}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <span className="muted small">No group of 5 or more for this filter.</span>
      )}
    </section>
  );
}

function AnalyticsTake() {
  const { toast } = useMe();
  const { data, reload } = useLoad<any>("/api/insights/analytics");
  const [busy, setBusy] = useState(false);
  const [more, setMore] = useState(false);
  if (!data) return null;
  const run = async () => {
    setBusy(true);
    try {
      await api("/api/insights/analytics", { method: "POST" });
      reload();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  const ins = data.insight?.result;
  const label = (t: string, color = "var(--accent-ink)") => <div className="tiny" style={{ fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color, marginBottom: 4 }}>{t}</div>;
  return (
    <section className="card stack ai-card">
      <div className="row between">
        <div>
          <span className="ai-spark" /><strong className="ai-mark">Claude's read for the roadmap</strong>
          {data.insight && <span className="muted tiny"> · {ago(data.insight.created_at)} · AI suggestion, check it</span>}
        </div>
        {data.can_refresh && data.connected && <button className="btn sm" disabled={busy} onClick={run}>{busy ? "Thinking…" : data.insight ? "Refresh" : "Ask Claude what the data says"}</button>}
      </div>
      {!data.connected ? (
        <span className="muted small">AI isn't connected yet.</span>
      ) : !ins ? (
        <span className="muted small">Claude reads these numbers next to the open roadmap (GitHub themes, milestones and work items) and says which work the usage supports, what it argues against, and what to measure next. About 10 cents a run.</span>
      ) : (
        <>
          <div style={{ fontSize: 15 }}>{ins.headline}</div>
          <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))" }}>
            <div>
              {label("What the data supports")}
              {ins.this_week.map((x: any, i: number) => (
                <div key={i} style={{ padding: "8px 6px" }}>
                  <div className="row between" style={{ gap: 8 }}><span style={{ fontWeight: 600, fontSize: 14 }}>{x.title}</span><Pill>{x.who}</Pill></div>
                  <div className="small muted">{x.why}</div>
                  <div className="small" style={{ marginTop: 2 }}><span style={{ color: "var(--accent-ink)", fontWeight: 600 }}>Next: </span>{x.next_step}</div>
                </div>
              ))}
            </div>
            <div>
              {label("Warning signs", "var(--muted)")}
              {ins.at_risk.map((x: any, i: number) => (
                <div key={i} style={{ padding: "8px 6px" }}><div style={{ fontWeight: 600, fontSize: 14 }}>{x.title}</div><div className="small muted">{x.why}</div></div>
              ))}
            </div>
          </div>
          {ins.watch_out && <div className="banner warn small"><div><strong>Watch out: </strong>{ins.watch_out}</div></div>}
          {ins.not_now.length > 0 && (
            <div>
              <button className="btn ghost sm" style={{ padding: 0 }} onClick={() => setMore(!more)}>{more ? "Hide" : `The data argues against (${ins.not_now.length})`}</button>
              {more && ins.not_now.map((x: any, i: number) => <div key={i} style={{ padding: "8px 6px" }}><div style={{ fontWeight: 600, fontSize: 14 }}>{x.title}</div><div className="small muted">{x.why}</div></div>)}
            </div>
          )}
        </>
      )}
    </section>
  );
}
